import {FC, useEffect, useState} from "react";
import {Info, AlertCircle, Loader2, RefreshCw, Coins} from "lucide-react";
import axios from "axios";
import {useToast} from "@/components/ui/use-toast";
import CustomBreadcrumbs from "@/components/Breadcrumb";
import {Link, useLocation} from "react-router-dom";
import {useError} from "@/contexts/ErrorProvider";
import {useSimpleTour} from "@/contexts/SimpleTourProvider";
import RoundedIcon from "./dashboard/RoundedIcon";
import CAlert from "./CAlert";
import config from "@/config";

interface HeaderProps {
}

const Header: FC<HeaderProps> = () => {
    const {pathname} = useLocation();
    const [coins, setCoins] = useState(0);
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const {toast} = useToast();
    const {isError, isVisible} = useError();
    const {isActive, toggleTour} = useSimpleTour();

    useEffect(() => {
        const fetchCoins = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("accessToken");

                const res = await axios.get(
                    `${config.NEXT_PUBLIC_BASE_URL}/api/users/coins`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`,
                        },
                    }
                );

                if (res.status === 200) {
                    setCoins(parseInt(res.data.shredsCoins) || 0);
                    setStatus(res.data.status || "NO_SUBSCRIPTION");
                } else {
                    setError("Invalid credentials");
                    toast({
                        title: "ERROR",
                        description: "Invalid credentials",
                    });
                }
            } catch (error) {
                setError("Sorry something went wrong. Please try again later.");
                toast({
                    title: "ERROR",
                    description: "Sorry something went wrong. Please try again later.",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCoins();
    }, [pathname, toast]);

    const renderSubscriptionStatus = () => {
        if (loading) {
            return (
                <div className="bg-gray-200 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-gray-300 text-[10px] sm:text-xs">
                    <Loader2 className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 animate-spin flex-shrink-0"/>
                    <span className="font-medium truncate hidden sm:inline">Loading...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="bg-red-200 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-red-300 text-[10px] sm:text-xs">
                    <AlertCircle className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0"/>
                    <span className="font-medium truncate hidden sm:inline">Error</span>
                    <button
                        onClick={() => window.location.reload()}
                        className="text-blue-600 font-bold ml-1 sm:ml-2 underline text-[10px] sm:text-xs flex items-center flex-shrink-0"
                    >
                        <RefreshCw className="h-2.5 w-2.5 sm:h-3 sm:w-3"/>
                    </button>
                </div>
            );
        }

        // Always show credits if they exist
        const showCredits = coins > 0;

        switch (status) {
            case "ACTIVE":
                return (
                    <div className="bg-green-200 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-green-300 text-[10px] sm:text-xs">
                        <Coins className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0"/>
                        <span className="font-medium truncate hidden sm:inline">
                            Active:
                        </span>
                        <span className="text-blue-500 font-bold ml-1 flex-shrink-0">{coins}</span>
                        <Link to="/pricing/plans" className="text-blue-600 font-bold ml-1 sm:ml-2 underline text-[10px] sm:text-xs flex-shrink-0 hidden sm:inline">
                            Add
                        </Link>
                    </div>
                );

            case "ACTIVE_UNTIL_PERIOD_END":
                return (
                    <div className="bg-yellow-200 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-yellow-300 text-[10px] sm:text-xs">
                        <Info className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0"/>
                        <span className="font-medium truncate hidden sm:inline">
                            Canceling:
                        </span>
                        <span className="text-blue-500 font-bold ml-1 flex-shrink-0">{coins}</span>
                        <Link to="/pricing/plans" className="text-blue-600 font-bold ml-1 sm:ml-2 underline text-[10px] sm:text-xs flex-shrink-0 hidden sm:inline">
                            Add
                        </Link>
                    </div>
                );

            case "CANCELED":
                return (
                    <div className="bg-orange-200 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-orange-300 text-[10px] sm:text-xs">
                        <Info className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0"/>
                        <span className="font-medium truncate hidden sm:inline">
                            Ended:
                        </span>
                        <span className="text-blue-500 font-bold ml-1 flex-shrink-0">{coins}</span>
                        <Link to="/pricing/plans" className="text-blue-600 font-bold ml-1 sm:ml-2 underline text-[10px] sm:text-xs flex-shrink-0">
                            Subscribe
                        </Link>
                    </div>
                );

            case "PAST_DUE":
                return (
                    <div className="bg-red-200 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-red-300 text-[10px] sm:text-xs">
                        <AlertCircle className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0"/>
                        <span className="font-medium truncate hidden sm:inline">
                            Past Due:
                        </span>
                        <span className="text-blue-500 font-bold ml-1 flex-shrink-0">{coins}</span>
                        <Link to="/settings" className="text-blue-600 font-bold ml-1 sm:ml-2 underline text-[10px] sm:text-xs flex-shrink-0">
                            Update
                        </Link>
                    </div>
                );

            case "NO_SUBSCRIPTION":
            default:
                return (
                    <div className="bg-red-100/80 text-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center border border-red-200 text-[10px] sm:text-xs">
                        <Info className="mr-0.5 sm:mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0"/>
                        <span className="font-medium truncate hidden sm:inline">
                            No Subscription
                        </span>
                        <span className="font-medium truncate sm:hidden">
                            No Sub
                        </span>
                        {showCredits && (
                            <>
                                <span className="text-blue-500 font-bold ml-1 flex-shrink-0 hidden sm:inline">Credits: {coins}</span>
                                <div className="mx-1 flex-shrink-0 hidden sm:inline">|</div>
                            </>
                        )}
                        <Link to="/pricing/plans" className="text-blue-500 ml-1 font-bold underline text-[10px] sm:text-xs flex-shrink-0">
                            <span className="hidden sm:inline">Subscribe</span>
                            <span className="sm:hidden">Sub</span>
                        </Link>
                    </div>
                );
        }
    };

    return (
        <div className="h-auto w-full max-w-7xl mx-auto">
            {/* Main Header Content */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                {/* Left Side - Breadcrumb */}
                <div className="flex items-center flex-wrap gap-2">
                    <RoundedIcon color="#6F6AF8" height="h-8 sm:h-11" width="w-8 sm:w-11">
                        <img
                            height={32}
                            width={32}
                            src="/icons/folder.svg"
                            alt="Folder icon"
                            className="sm:h-[44px] sm:w-[44px] h-8 w-8"
                        />
                    </RoundedIcon>
                    <div className="flex items-center">
                        <h1 className="font-bold text-sm sm:text-base lg:text-2xl tracking-wide">
                            <Link to="/dashboard">HOME</Link> {" > "}{" "}
                            {pathname === "/dashboard" && (
                                <span className={"text-fuchsia-700"}>
                                </span>
                            )}
                        </h1>
                        <div className="ml-2">
                            <CustomBreadcrumbs/>
                        </div>
                    </div>
                </div>

                {/* Right Side - Controls */}
                <div className="flex flex-row items-center gap-3 lg:gap-4">
                    {/* Guide Toggle Switch */}
                    <div className="flex items-center space-x-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-700">Guide</span>
                        <button
                            onClick={toggleTour}
                            className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                isActive ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                            role="switch"
                            aria-checked={isActive}
                            aria-label="Toggle guide"
                        >
                            <span
                                className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out ${
                                    isActive ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Subscription Status */}
                    <div className="flex-shrink-0">
                        {renderSubscriptionStatus()}
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {isError !== "" && isVisible && (
                <div className="mb-4">
                    <CAlert
                        icon={<Info/>}
                        title="Info"
                        variant="danger"
                        body={isError}
                    />
                </div>
            )}
        </div>
    );
};

export default Header;